import {
  ChangeDetectorRef,
  Component,
  effect,
  ElementRef,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  QueryList,
  signal,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  Validators,
} from '@angular/forms';
import {
  DefaultProfileComponentResource,
  GenderEnum,
  ProfileComponentResources,
} from './profile.component.resources';

import { IUpdateUserInfo, IUserInfo } from '../../../models/user';
import { Store } from '@ngrx/store';
import {
  BehaviorSubject,
  filter,
  firstValueFrom,
  interval,
  Observable,
  Subject,
  Subscription,
  take,
  takeUntil,
} from 'rxjs';
import {
  selectIsNewUser,
  selectUserInfo,
} from '../../../store/user/user.reducer';
import {
  CheckUserPhoneOrEmailAction,
  CheckUserPhoneOrEmailSuccessAction,
  DeleteMobileAndUpdateUserAction,
  DeleteUserAccountAction,
  DeleteUserMobileAction,
  UpdateUserAction,
  UpdateUserFailureAction,
} from '../../../store/user/user.actions';
import { MediaDeliveryService } from '../../../services/mediaDelivery.services';
import { SnackBarService } from '../../../services/snackbar.service';
import { IErrors } from '../../../models/error';
import { FormValidators } from '../../../_helpers/form-validators';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogActions,
  MatDialogClose,
  MatDialogContent,
  MatDialogModule,
  MatDialogRef,
  MatDialogTitle,
} from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIcon, MatIconModule } from '@angular/material/icon';
import { NgOtpInputConfig, NgOtpInputModule } from 'ng-otp-input';
import { CommonModule } from '@angular/common';
import { Actions, ofType } from '@ngrx/effects';
import {
  GenerateOTPAction,
  GenerateOTPSuccessAction,
  VerifyOTPAction,
  VerifyOTPFailureAction,
  VerifyOTPSuccessAction,
} from '../../../store/auth/auth.actions';
import { IClientInfo } from '../../../models/authentication';
import { selectClientInfo } from '../../../store/auth/auth.reducer';

export enum OtpStatus {
  Initial = 'Initial',
  Sent = 'Sent',
  Verified = 'Verified',
  Verifying = 'Verifying',
  Failed = 'Failed',
}

export interface VerificationItem {
  field: string;
  value: string;
  deliveryMode: 'EMAIL' | 'SMS';
  otpStatus: OtpStatus;
  otpVerificationToken: string | null;
  otpVerificationSuccessToken: string | null;
  expiresIn: number;
  otpStatusSubject?: BehaviorSubject<OtpStatus>;
  otpVerificationTokenSubject?: BehaviorSubject<string | null>;
  expiresInSubject?: BehaviorSubject<number>;
  verifyOtpSubject?: Subject<{
    verificationToken: string;
    userInput: string;
    receiver: string;
  }>;
  resendOtpSubject?: Subject<{
    receiver: string;
    deliveryMode: 'SMS' | 'EMAIL';
  }>;
}
import {
  getAutoNavigateOnAudio,
  setAutoNavigateOnAudio,
} from '../../../_helpers/audio-auto-navigate';
import {
  CountriesStatesService,
  ICountryInfoWithStates,
  IMotherTongue,
  IPreferredLanguage,
} from '../../../services/countries_states_service';

@Component({
  selector: 'app-profile',
  standalone: false,
  templateUrl: './profile.component.html',
  styleUrl: './profile.component.scss',
})
export class ProfileComponent implements OnInit, OnDestroy {
  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;
  @ViewChild('dobInput') dobInput!: ElementRef<HTMLInputElement>;
  maxDate: string = ''; // Property to hold today's date
  profileForm!: FormGroup;
  initialFormValues: any = {};
  userInfo: IUserInfo | null = null;
  isFormChanged = false;
  isEditing = signal(false);
  uploadSuccess: boolean = false;
  isFileUploaded: boolean = false;
  selectedFile: File | null = null;
  viewImageUrl: string | null = null;
  uploadImageUrl: string | null = null;
  typedDobValue: string | null = null; // Store raw typed DOB value
  // Track drag state
  private isDragging = false;
  private otpDialogRef: MatDialogRef<ProfileOTPDialogComponent> | null = null;
  allowMobileOtp: boolean = true;
  allowEmailOtp: boolean = true;
  private destroy$ = new Subject<void>();

  countriesOptions: ICountryInfoWithStates[] = [];
  statesOptions: string[] = [];
  // Provide Address Toggle
  provideAddress: boolean = false;
  autoNavigateOnAudio: boolean = getAutoNavigateOnAudio();

  // Observables
  userInfo$: Observable<IUserInfo | null>;
  isNewUser$: Observable<boolean>;

  @Input() resources: Partial<ProfileComponentResources> = {};
  finalResources!: ProfileComponentResources;
  private _subscriptions: Subscription[] = [];

  private profileFormDetails: { [key: string]: any } = {};
  private userChanges: Partial<{ [key: string]: any }> = {};
  private verificationItems: VerificationItem[] = [];
  private completeSubject?: Subject<void>;
  private isOtpDialogOpen: boolean = false;
  clientInfo$: Observable<IClientInfo | null>;
  clientInfo: IClientInfo | null;
  private _componentSubscriptions: Subscription[] = [];
  private verificationCompleted: boolean = false;

  // Dropdown Options
  motherTongueOptions: IMotherTongue[] = [];
  preferredLanguagesOptions: IPreferredLanguage[] = [];
  genderOptions = Object.values(GenderEnum);

  constructor(
    private fb: FormBuilder,
    private store: Store,
    private dialog: MatDialog,
    private mediaDeliveryService: MediaDeliveryService,
    private snackBarService: SnackBarService,
    private actions$: Actions,
    private countriesStatesService: CountriesStatesService,
    private cdr: ChangeDetectorRef,
  ) {
    this.userInfo$ = this.store.select(selectUserInfo);
    this.isNewUser$ = this.store.select(selectIsNewUser);
    this.clientInfo$ = this.store.select(selectClientInfo);
    this.clientInfo = null;

    // Enable/Disable IsMarketingOptedIn checkbox based on isEditing value
    effect(() => {
      const cbx = this.profileForm.get('IsMarketingOptedIn');
      if (this.isEditing()) {
        cbx?.enable();
      } else {
        cbx?.disable();
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this._subscriptions.forEach((sub) => sub.unsubscribe());
  }

  getGenderLabel(textValue: string): string {
    const option = this.genderOptions.find(
      (opt) => opt.textValue === textValue,
    );
    return option ? option.value : '-';
  }

  triggerValidation(controlName: string): void {
    const control = this.profileForm.get(controlName);
    if (control) {
      control.markAsTouched(); // Mark field as touched
      control.updateValueAndValidity({ onlySelf: true }); // Recalculate validity
    }
  }
  onEditProfileImage(): void {
    this.fileInput.nativeElement.click();
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];
      const validTypes = ['image/jpeg', 'image/png'];
      if (validTypes.includes(file.type) && file.size <= 10 * 1024 * 1024) {
        this.selectedFile = file;
        this.isFileUploaded = true;
        this.uploadProfileImage(file);
      } else {
        switch (file.type) {
          case 'image/gif':
            this.snackBarService.showSnackBar(
              'error',
              'GIF files are not supported',
            );
            break;
          default:
            this.snackBarService.showSnackBar(
              'error',
              'Only JPG or PNG files up to 10MB are allowed',
            );
        }
        this.selectedFile = null;
        input.value = ''; // Reset file input
      }
    }
  }

  onImageError(event: Event) {
    (event.target as HTMLImageElement).src =
      'assets/images/default-profile.png';
  }

  uploadProfileImage(file: File): void {
    this.mediaDeliveryService.profileUpload(file).subscribe({
      // Modified to pass FormData
      next: (response) => {
        this.uploadSuccess = true;
        this.uploadImageUrl = response.Value.FileUrl;
        this.viewImageUrl = this.mediaDeliveryService.getDownloadUrl(
          response.Value.FileUrl,
        );
        this.profileForm.markAsDirty(); // Mark form as dirty so save button is enabled
        this.profileForm.updateValueAndValidity(); // Update form validity
      },
      error: (err: IErrors) => {
        this.snackBarService.showSnackBar(
          'error',
          err?.Errors?.[0]?.Message || 'Uploading Profile Image Failed',
        );
        this.isFileUploaded = false;
        this.selectedFile = null; // Reset selected file on error
        this.fileInput.nativeElement.value = ''; // Reset file input on error
      },
    });
  }

  // Handle Date Of Birth Change
  onDobChange(): void {
    const dobControl = this.profileForm.get('dob');
    if (dobControl) {
      // Update typedDobValue with the final selected value
      this.typedDobValue = dobControl.value || null;
      dobControl.markAsTouched();
      dobControl.updateValueAndValidity({ onlySelf: true });
    }
  }

  // Get Country Name By Country Code
  getCountryName(code: string | null | undefined): string {
    if (!code) return '-';
    const country = this.countriesOptions.find(
      (country) => country.code === code,
    );
    return country ? country.name : '-';
  }

  // Update toggleEdit to reload states when entering edit mode if country is prefilled
  toggleEdit(): void {
    if (this.isEditing()) {
      // Reset form to initial values on cancel
      this.profileForm.reset(this.initialFormValues, { emitEvent: false });
      this.isFormChanged = false;
      this.isFileUploaded = false;
      this.selectedFile = null;
      this.uploadImageUrl = null;
      this.viewImageUrl = this.userInfo?.PictureUrl
        ? this.mediaDeliveryService.getDownloadUrl(this.userInfo.PictureUrl)
        : null;
      // Disable primaryAddress fields when exiting edit mode
      const primaryAddressGroup = this.profileForm.get(
        'primaryAddress',
      ) as FormGroup;
      ['line1', 'line2', 'city', 'postalCode', 'country', 'state'].forEach(
        (field) => {
          const control = primaryAddressGroup.get(field);
          control?.disable({ emitEvent: false });
        },
      );
      this.provideAddress = false;
    } else {
      // Store current values before editing, including disabled fields
      this.initialFormValues = { ...this.profileForm.getRawValue() };

      // Refresh statesOptions if country is prefilled, regardless of provideAddress
      const primaryAddressGroup = this.profileForm.get(
        'primaryAddress',
      ) as FormGroup;
      const countryControl = primaryAddressGroup.get('country');
      if (countryControl?.value) {
        this.countriesStatesService
          .getStatesByCountry(countryControl.value)
          .subscribe((states) => {
            this.statesOptions = states;
            const stateControl = primaryAddressGroup.get('state');
            const currentStateValue = stateControl?.value;
            if (currentStateValue && states.includes(currentStateValue)) {
              // Preserve the current state value if valid
              stateControl?.setValue(currentStateValue, { emitEvent: false });
            } else {
              // Clear if invalid
              stateControl?.setValue('', { emitEvent: false });
            }
            this.cdr.detectChanges(); // Ensure UI updates with new options and value
          });
      }

      // Enable primaryAddress fields only if provideAddress is true
      if (this.provideAddress) {
        ['line1', 'line2', 'city', 'postalCode', 'country', 'state'].forEach(
          (field) => {
            const control = primaryAddressGroup.get(field);
            control?.enable({ emitEvent: false });
          },
        );
      }

      // Validate all fields immediately
      Object.keys(this.profileForm.controls).forEach((field) => {
        const control = this.profileForm.get(field);
        control?.markAsTouched();
        control?.updateValueAndValidity({ onlySelf: true });
      });
    }
    this.isEditing.update((v) => !v);
    this.viewImageUrl = this.mediaDeliveryService.getDownloadUrl(
      this.userInfo?.PictureUrl!,
    );
  }

  noOnlySpacesValidator() {
    return (control: any) => {
      const value = control.value;
      if (value && value.trim().length === 0) {
        return { onlySpaces: true }; // Error if input is only spaces
      }
      return null; // Valid input
    };
  }

  // Method to show error message for full name (show only one at a time)
  getFullNameErrorMessage(): string {
    const control = this.profileForm.get('fullName');
    if (control?.errors) {
      const errors = control.errors;
      if (errors['required']) return this.finalResources.fullNameRequiredErrMsg;
      if (errors['onlySpaces'])
        return this.finalResources.fullNameOnlySpacesErrMsg;
      if (errors['pattern']) return this.finalResources.invalidFullNameErrMsg;
      if (errors['minlength'] || errors['minlengthNoSpaces'])
        return this.finalResources.fullNameMinCharactersErrMsg;
      if (errors['maxlength'] || errors['maxlengthNoSpaces'])
        return this.finalResources.fullNameMaxCharactersErrMsg;
    }
    return '';
  }

  // Method to show error message for address (show only one at a time)
  getAddressErrorMessage(): string {
    const control = this.profileForm.get('address');
    if (control?.errors) {
      const errors = control.errors;
      if (errors['onlySpaces'])
        return this.finalResources.addressOnlySpacesErrMsg;
      if (errors['minlength'] || errors['minlengthNoSpaces'])
        return this.finalResources.addressMinCharactersRequired;
      if (errors['maxlength'] || errors['maxlengthNoSpaces'])
        return this.finalResources.addressMaxCharactersRequired;
    }
    return '';
  }

  // Method to show error message for DOB(show only one at a time)
  getDobErrorMessage(): string {
    const control = this.profileForm.get('dob');
    if (control?.touched && (control?.invalid || control?.errors)) {
      const errors = control.errors;
      if (errors) {
        if (errors['futureDate'])
          return this.finalResources.dobFutureDateErrMsg;
        if (errors['unrealisticDate'])
          return this.finalResources.dobUnrealisticDateErrMsg;
        if (errors['underage']) return this.finalResources.dobUnderageErrMsg;
      }
    }
    return '';
  }

  // Method to show error message for mobile (error message and validations same as register component)
  getMobileErrorMessage(): string {
    const mobileControl = this.profileForm.get('mobile');
    if (mobileControl?.errors) {
      if (
        mobileControl.errors['invalidMobile'] ||
        mobileControl.errors['pattern']
      ) {
        return this.finalResources.mobileNumberInvalidPatternErrMsg;
      }
    }
    return '';
  }

  // Method to show error message for email (error message and validations same as register component)
  getEmailErrorMessage(): string {
    const emailControl = this.profileForm.get('email');
    if (emailControl?.errors) {
      if (emailControl.errors['required']) {
        return this.finalResources.emailAddressRequiredErrMsg;
      }
      if (emailControl.errors['email'] || emailControl.errors['pattern']) {
        return this.finalResources.emailAddressInvalidPatternErrMsg;
      }
    }
    return '';
  }

  async ngOnInit(): Promise<void> {
    // Set the Component Resources
    this.finalResources = {
      ...DefaultProfileComponentResource,
      ...this.resources,
    };

    // Set maxDate to today's date in YYYY-MM-DD format
    const today = new Date();
    this.maxDate = today.toISOString().split('T')[0]; // e.g., "2025-05-30"

    // Initialize the Profile Form
    this.profileForm = this.fb.group({
      fullName: ['', FormValidators.fullNameValidators()],
      mobile: ['', FormValidators.mobileValidators()],
      email: ['', FormValidators.emailValidators()],
      dob: ['', FormValidators.dobValidator()],
      gender: [''],
      motherTongue: [''],
      preferredLanguage: [''],
      address: ['', FormValidators.addressValidators()],
      primaryAddress: new FormGroup({
        line1: new FormControl({ value: '', disabled: true }),
        line2: new FormControl({ value: '', disabled: true }),
        city: new FormControl({ value: '', disabled: true }),
        postalCode: new FormControl({ value: '', disabled: true }),
        country: new FormControl({ value: '', disabled: true }),
        state: new FormControl({ value: '', disabled: true }),
      }),
      IsMarketingOptedIn: [
        {
          value: false,
          disabled: !this.isEditing(),
        },
      ],
    });

    this.setupAddressValidation();

    // Initially store the form details
    this.profileFormDetails = {
      fullName: this.profileForm.get('fullName')?.value,
      mobileNumber: this.profileForm.get('mobileNumber')?.value,
      email: this.profileForm.get('email')?.value,
    };

    Object.keys(this.profileForm.controls).forEach((field) => {
      this.profileForm.get(field)?.valueChanges.subscribe((value) => {
        if (value !== this.profileFormDetails[field]) {
          this.userChanges[field as keyof typeof this.profileFormDetails] =
            value;
        } else {
          delete this.userChanges[
            field as keyof typeof this.profileFormDetails
          ];
        }
      });
    });

    this.finalResources = {
      ...DefaultProfileComponentResource,
      ...this.resources,
    };

    this.userInfo = await firstValueFrom(
      this.store.select(selectUserInfo).pipe(filter(Boolean)),
    );

    if (this.userInfo) {
      const userValues: any = {};

      // Only set values if they are not null/undefined
      if (this.userInfo.Fullname) userValues.fullName = this.userInfo.Fullname;
      if (this.userInfo.Mobile) userValues.mobile = this.userInfo.Mobile;
      if (this.userInfo.Email) userValues.email = this.userInfo.Email;
      if (this.userInfo.Dob) userValues.dob = this.userInfo.Dob;
      if (this.userInfo.MotherTongue)
        userValues.motherTongue = this.userInfo.MotherTongue;
      if (this.userInfo.PreferredLanguage)
        userValues.preferredLanguage = this.userInfo.PreferredLanguage;
      if (this.userInfo.Gender) userValues.gender = this.userInfo.Gender;
      if (this.userInfo.Address) userValues.address = this.userInfo.Address;
      if (this.userInfo.PrimaryAddress) {
        userValues.primaryAddress = {
          line1: this.userInfo.PrimaryAddress.Line1 || '',
          line2: this.userInfo.PrimaryAddress.Line2 || '',
          city: this.userInfo.PrimaryAddress.City || '',
          postalCode: this.userInfo.PrimaryAddress.PostalCode || '',
          country: this.userInfo.PrimaryAddress.Country || '',
          state: this.userInfo.PrimaryAddress.State || '',
        };
      }
      if (this.userInfo.PictureUrl) {
        this.viewImageUrl = this.mediaDeliveryService.getDownloadUrl(
          this.userInfo.PictureUrl,
        );
      }
      userValues.IsMarketingOptedIn =
        this.userInfo.Declarations?.IsMarketingOptedIn ?? false;

      this.profileForm.patchValue(userValues);

      // If country is prefilled, fetch states and set state value
      if (this.userInfo.PrimaryAddress?.Country) {
        await firstValueFrom(
          this.countriesStatesService.getStatesByCountry(
            this.userInfo.PrimaryAddress.Country,
          ),
        ).then((states) => {
          this.statesOptions = states;
          const stateControl = this.profileForm.get('primaryAddress.state');
          if (
            this.userInfo &&
            this.userInfo.PrimaryAddress?.State &&
            states.includes(this.userInfo.PrimaryAddress.State)
          ) {
            stateControl?.setValue(this.userInfo.PrimaryAddress.State, {
              emitEvent: false,
            });
          } else {
            stateControl?.setValue('', { emitEvent: false });
          }
          this.cdr.detectChanges();
        });
      }
    }

    // Track form changes for save button
    this.profileForm.valueChanges.subscribe(() => {
      this.isFormChanged =
        JSON.stringify(this.profileForm.getRawValue()) !==
        JSON.stringify(this.initialFormValues); // Compare Using Raw Values
    });

    // Set the Countries Options From the Service
    this.countriesStatesService.getAllCountries().subscribe((countries) => {
      this.countriesOptions = countries;
      this.cdr.detectChanges(); // Update the UI
    });

    // Set the Mother Tongue Options
    this.countriesStatesService
      .getAllMotherTongues()
      .subscribe((motherTongues) => {
        this.motherTongueOptions = motherTongues;
        this.cdr.detectChanges(); // Update the UI
      });

    // Set the Preferred Language Options
    this.countriesStatesService
      .getAllPreferredLanguages()
      .subscribe((preferredLanguages) => {
        this.preferredLanguagesOptions = preferredLanguages;
        this.cdr.detectChanges(); // Update the UI
      });

    this._subscriptions.push(
      this.userInfo$.subscribe(async (userInfo) => {
        if (userInfo) {
          this.userInfo = await firstValueFrom(
            this.store.select(selectUserInfo).pipe(filter(Boolean)),
          );
        }
        if (this.isEditing()) {
          this.isEditing.update((v) => !v);
        }
        this.provideAddress = false; // Make the Toggle As False
      }),
    );

    // Handle client info for enabling/disabling fields
    this._subscriptions.push(
      this.clientInfo$.subscribe((clientInfo) => {
        if (!clientInfo) return;
        this.clientInfo = clientInfo;

        const mobileCtrl = this.profileForm.get('mobile');
        const emailCtrl = this.profileForm.get('email');

        this.allowMobileOtp = !!clientInfo.LoginMethodsAllowed?.MobileOTP;
        this.allowEmailOtp = !!clientInfo.LoginMethodsAllowed?.EmailOTP;

        if (!this.allowMobileOtp) {
          mobileCtrl?.disable({ emitEvent: false });
        } else {
          mobileCtrl?.enable({ emitEvent: false });
        }

        if (!this.allowEmailOtp) {
          emailCtrl?.disable({ emitEvent: false });
        } else {
          emailCtrl?.enable({ emitEvent: false });
        }
        this.provideAddress = false; // Make the Toggle As False
      }),
    );

    // Handle update failure
    this._subscriptions.push(
      this.actions$
        .pipe(ofType(UpdateUserFailureAction), takeUntil(this.destroy$))
        .subscribe(({ error }) => {
          if (this.isOtpDialogOpen && this.otpDialogRef) {
            this.otpDialogRef.close(); // Close the otp dialog
          }
        }),
    );

    this._subscriptions.push(
      this.userInfo$.subscribe(async (userInfo) => {
        if (userInfo) {
          this.userInfo = await firstValueFrom(
            this.store.select(selectUserInfo).pipe(filter(Boolean)),
          );
        }
        if (this.isEditing()) {
          this.isEditing.update((v) => !v);
        }
      }),
    );
  }

  private verifyChangedFields() {
    this.verificationItems.forEach((item) => {
      item.otpStatusSubject = new BehaviorSubject<OtpStatus>(item.otpStatus);
      item.otpVerificationTokenSubject = new BehaviorSubject<string | null>(
        item.otpVerificationToken,
      );
      item.expiresInSubject = new BehaviorSubject<number>(item.expiresIn);
      item.verifyOtpSubject = new Subject<{
        verificationToken: string;
        userInput: string;
        receiver: string;
      }>();
      item.resendOtpSubject = new Subject<{
        receiver: string;
        deliveryMode: 'SMS' | 'EMAIL';
      }>();
    });

    this.verificationItems.forEach((item) => {
      this._componentSubscriptions.push(
        item.verifyOtpSubject!.subscribe((data) => {
          item.otpStatusSubject!.next(OtpStatus.Verifying);
          this.store.dispatch(
            VerifyOTPAction({
              request: {
                VerificationToken: data.verificationToken,
                UserInput: data.userInput,
                Receiver: data.receiver,
              },
            }),
          );

          const verifySub = this.actions$
            .pipe(
              ofType(VerifyOTPSuccessAction, VerifyOTPFailureAction),
              take(1),
            )
            .subscribe((action) => {
              if (action.type === VerifyOTPSuccessAction.type) {
                const response = (
                  action as ReturnType<typeof VerifyOTPSuccessAction>
                ).response;
                item.otpVerificationSuccessToken = response.Value.Token;
                item.otpStatusSubject!.next(OtpStatus.Verified);
                this.snackBarService.showSnackBar(
                  'success',
                  'OTP Verified Successfully.',
                );
              } else {
                item.otpStatusSubject!.next(OtpStatus.Failed);
                item.otpVerificationSuccessToken = null;
              }
            });
          this._componentSubscriptions.push(verifySub);
        }),
      );

      this._componentSubscriptions.push(
        item.resendOtpSubject!.subscribe((data) => {
          this.store.dispatch(
            GenerateOTPAction({
              request: {
                Receiver: data.receiver,
                DeliveryMode: data.deliveryMode,
              },
            }),
          );

          const resendSub = this.actions$
            .pipe(ofType(GenerateOTPSuccessAction), take(1))
            .subscribe(({ response }) => {
              item.otpStatus = OtpStatus.Sent;
              item.otpVerificationToken = response.Value.VerificationToken;
              item.expiresIn = response.Value.ExpiresIn || 120;
              item.otpStatusSubject!.next(item.otpStatus);
              item.otpVerificationTokenSubject!.next(item.otpVerificationToken);
              item.expiresInSubject!.next(item.expiresIn);
            });
          this._componentSubscriptions.push(resendSub);
        }),
      );
    });

    this.generateOtpsSequentially(0);
  }

  private generateOtpsSequentially(index: number) {
    if (index >= this.verificationItems.length) {
      this.openVerificationDialog();
      return;
    }

    const item = this.verificationItems[index];
    this.store.dispatch(
      GenerateOTPAction({
        request: {
          Receiver: item.value,
          DeliveryMode: item.deliveryMode,
        },
      }),
    );

    const genSub = this.actions$
      .pipe(ofType(GenerateOTPSuccessAction), take(1))
      .subscribe(({ response }) => {
        item.otpStatus = OtpStatus.Sent;
        item.otpVerificationToken = response.Value.VerificationToken;
        item.expiresIn = response.Value.ExpiresIn || 120;
        item.otpStatusSubject!.next(item.otpStatus);
        item.otpVerificationTokenSubject!.next(item.otpVerificationToken);
        item.expiresInSubject!.next(item.expiresIn);
        this.generateOtpsSequentially(index + 1);
      });
    this._componentSubscriptions.push(genSub);
  }

  private openVerificationDialog() {
    this.completeSubject = new Subject<void>();
    this._componentSubscriptions.push(
      this.completeSubject.subscribe(() => {
        this.verificationCompleted = true;
        const updatePayload: any = {};
        this.verificationItems.forEach((item) => {
          if (item.otpVerificationSuccessToken) {
            const fieldToUpdate = item.field === 'email' ? 'email' : 'mobile';
            updatePayload[fieldToUpdate] = item.otpVerificationSuccessToken;
          }
        });

        // Add non-verifiable changed fields (e.g., fullName, address, PictureUrl) if any
        Object.keys(this.userChanges).forEach((key) => {
          if (
            key !== 'email' &&
            key !== 'mobile' &&
            this.userChanges[key as keyof typeof this.profileFormDetails] !==
              this.profileFormDetails[key]
          ) {
            updatePayload[key] =
              this.userChanges[key as keyof typeof this.profileFormDetails];
          }
        });

        if (this.isFileUploaded && this.selectedFile && this.uploadImageUrl) {
          updatePayload['PictureUrl'] = this.uploadImageUrl;
        }
        if (
          'mobile' in this.userChanges &&
          !this.userChanges['mobile'] &&
          this.initialFormValues.mobile != this.userChanges['mobile']
        ) {
          // remove mobile number if needed
          this.deleteUserMobileAndUpdateUser({
            request: updatePayload,
            userId: this.userInfo!.UserId!,
          });
        } else {
          this.store.dispatch(
            UpdateUserAction({
              request: updatePayload,
              userId: this.userInfo!.UserId!,
            }),
          );
        }
        this.otpDialogRef && this.otpDialogRef.close();
      }),
    );

    const dialogData = {
      verificationItems: this.verificationItems.map((item) => ({
        fieldName: item.field,
        fieldValue: item.value,
        otpStatusSubject: item.otpStatusSubject,
        otpVerificationTokenSubject: item.otpVerificationTokenSubject,
        expiresInSubject: item.expiresInSubject,
        verifyOtpSubject: item.verifyOtpSubject,
        resendOtpSubject: item.resendOtpSubject,
      })),
      completeSubject: this.completeSubject,
    };

    this.otpDialogRef = this.dialog.open(ProfileOTPDialogComponent, {
      data: dialogData,
      disableClose: true,
    });

    this.isOtpDialogOpen = true;

    this.otpDialogRef.afterClosed().subscribe(() => {
      this.isOtpDialogOpen = false;
      if (!this.verificationCompleted) {
        this.isFileUploaded = false;
        this.selectedFile = null;
        this.uploadImageUrl = null;
      }
    });
  }

  private setupAddressValidation(): void {
    const primaryAddressGroup = this.profileForm.get(
      'primaryAddress',
    ) as FormGroup;

    // Subscribe to country changes
    primaryAddressGroup
      .get('country')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((countryCode) => {
        if (this.provideAddress && countryCode) {
          this.countriesStatesService
            .getStatesByCountry(countryCode)
            .subscribe((states) => {
              this.statesOptions = states;
              primaryAddressGroup.get('state')?.setValue('');
              this.cdr.detectChanges();
            });
        } else {
          this.statesOptions = [];
          primaryAddressGroup.get('state')?.setValue('');
          this.cdr.detectChanges();
        }
      });
  }

  onAutoNavigateOnAudioToggle(checked: boolean): void {
    this.autoNavigateOnAudio = checked;
    setAutoNavigateOnAudio(checked);
  }

  onProvideAddressToggle(checked: boolean): void {
    this.provideAddress = checked;
    const primaryAddressGroup = this.profileForm.get(
      'primaryAddress',
    ) as FormGroup;
    const initialPrimaryAddress = this.initialFormValues.primaryAddress || {};
    ['line1', 'line2', 'city', 'postalCode', 'country', 'state'].forEach(
      (field) => {
        const control = primaryAddressGroup.get(field);
        if (checked && this.isEditing()) {
          control?.enable({ emitEvent: false });
          switch (field) {
            case 'line1':
              control?.setValidators(FormValidators.addressLine1Validators());
              break;
            case 'line2':
              control?.setValidators(FormValidators.addressLine2Validators());
              break;
            case 'city':
              control?.setValidators(FormValidators.cityValidators());
              break;
            case 'postalCode':
              control?.setValidators(FormValidators.postalCodeValidators());
              break;
            case 'country':
              control?.setValidators(FormValidators.countryValidators());
              break;
            case 'state':
              control?.setValidators(FormValidators.stateValidators());
              break;
          }
        } else {
          control?.disable({ emitEvent: false });
          control?.clearValidators();
          // Restore initial value instead of setting to empty string
          const initialValue = initialPrimaryAddress[field] || '';
          control?.setValue(initialValue, { emitEvent: false });
        }
        control?.updateValueAndValidity({ emitEvent: false });
      },
    );
    primaryAddressGroup.updateValueAndValidity({ emitEvent: false });
    // Explicitly trigger change detection to update UI
    this.cdr.detectChanges();
  }

  // ✅ Check if any form value has changed
  hasFormChanged(): boolean {
    return Object.keys(this.profileForm.controls).some((key) => {
      if (key === 'primaryAddress') {
        if (!this.provideAddress) return false; // Ignore primaryAddress if provideAddress is false
        const currentValue = this.profileForm.get(key)?.getRawValue();
        const initialValue = this.initialFormValues[key] || {};
        return Object.keys(currentValue).some(
          (subKey) => currentValue[subKey] !== (initialValue[subKey] || ''),
        );
      }
      return (
        this.profileForm.get(key)?.getRawValue() !== this.initialFormValues[key]
      );
    });
  }

  endEditing() {
    this.isEditing.update((v) => false);
  }

  deleteAccount(): void {
    const dialogRef = this.dialog.open(ConfirmDeleteAccountDialogComponent, {
      width: '400px',
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result === true) {
        this.store.dispatch(
          DeleteUserAccountAction({
            userId: this.userInfo!.UserId!,
          }),
        );
      }
    });
  }

  // Handle dragenter event
  onDragEnter(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    if (!this.isDragging) {
      this.isDragging = true;
      (event.currentTarget as HTMLElement).classList.add('drag-active');
    }
  }

  // Handle dragover event
  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    event.dataTransfer!.dropEffect = 'copy';
    if (!this.isDragging) {
      this.isDragging = true;
      (event.currentTarget as HTMLElement).classList.add('drag-active');
    }
  }

  // Handle dragleave event
  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    // Check if the drag is leaving the upload-box entirely
    const relatedTarget = event.relatedTarget as Node;
    const currentTarget = event.currentTarget as HTMLElement;
    if (!relatedTarget || !currentTarget.contains(relatedTarget)) {
      this.isDragging = false;
      currentTarget.classList.remove('drag-active');
    }
  }

  // Handle drop event
  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;
    (event.currentTarget as HTMLElement).classList.remove('drag-active');
    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      const file = files[0];
      const validTypes = ['image/jpeg', 'image/png'];
      if (validTypes.includes(file.type) && file.size <= 10 * 1024 * 1024) {
        this.selectedFile = file;
        this.isFileUploaded = true;
        this.uploadProfileImage(file);
      } else {
        switch (file.type) {
          case 'image/gif':
            this.snackBarService.showSnackBar(
              'error',
              'GIF files are not supported',
            );
            break;
          default:
            this.snackBarService.showSnackBar(
              'error',
              'Only JPG or PNG files up to 10MB are allowed',
            );
        }
      }
    }
  }

  // delete user mobile number
  private deleteUserMobileAndUpdateUser(userData: {
    request: IUpdateUserInfo;
    userId: string;
  }) {
    const keys = Object.keys(userData.request);
    if (keys.length === 1 && keys[0] === 'mobile') {
      this.store.dispatch(DeleteUserMobileAction());
    } else {
      this.store.dispatch(DeleteMobileAndUpdateUserAction(userData));
    }
  }

  // Check if the changed mobile/email already exists for some other user
  private checkNextVerifiableField(index: number): void {
    if (index >= this.verificationItems.length) {
      // All checks passed, proceed with OTP verification
      this.verifyChangedFields();
      return;
    }

    const item = this.verificationItems[index];
    const field = item.field === 'email' ? 'email' : 'mobile';

    this.store.dispatch(
      CheckUserPhoneOrEmailAction({
        mobileOrEmail: item.value,
        field: field,
      }),
    );

    // Wait for the check result
    const checkSub = this.actions$
      .pipe(ofType(CheckUserPhoneOrEmailSuccessAction), take(1))
      .subscribe(({ response, field: checkedField }) => {
        if (response.Value) {
          // Field already exists, show error and stop
          const fieldName =
            checkedField === 'email' ? 'email address' : 'mobile number';
          this.snackBarService.showSnackBar(
            'error',
            `An account with this ${fieldName} already exists. Please use a different one.`,
          );
          // Don't proceed further
        } else {
          // Field is available, check next field
          this.checkNextVerifiableField(index + 1);
        }
      });

    this._componentSubscriptions.push(checkSub);
  }

  // Update onSave to include all primaryAddress subfields and PrimaryAddressId
  onSave(): void {
    if (
      this.profileForm.invalid ||
      (!this.isFormChanged && !this.isFileUploaded)
    )
      return;

    const changedFields: any = {};
    Object.keys(this.profileForm.controls).forEach((key) => {
      if (key === 'primaryAddress') {
        if (!this.provideAddress) return; // Skip if provideAddress is false
        const currentValue = this.profileForm.get(key)?.getRawValue();
        const initialValue = this.initialFormValues[key] || {};
        let hasSubFieldChanged = false;

        // Check each subfield of primaryAddress for changes
        ['line1', 'line2', 'city', 'postalCode', 'country', 'state'].forEach(
          (subKey) => {
            const trimmedValue =
              typeof currentValue[subKey] === 'string'
                ? currentValue[subKey].trim()
                : currentValue[subKey];
            const initialSubValue = initialValue[subKey] || '';
            if (trimmedValue !== initialSubValue) {
              hasSubFieldChanged = true;
            }
          },
        );

        // If any subfield changed, include all primaryAddress subfields
        if (hasSubFieldChanged) {
          changedFields[key] = {
            line1:
              typeof currentValue.line1 === 'string'
                ? currentValue.line1.trim()
                : currentValue.line1 || '',
            line2:
              typeof currentValue.line2 === 'string'
                ? currentValue.line2.trim()
                : currentValue.line2 || '',
            city:
              typeof currentValue.city === 'string'
                ? currentValue.city.trim()
                : currentValue.city || '',
            postalCode:
              typeof currentValue.postalCode === 'string'
                ? currentValue.postalCode.trim()
                : currentValue.postalCode || '',
            country:
              typeof currentValue.country === 'string'
                ? currentValue.country.trim()
                : currentValue.country || '',
            state:
              typeof currentValue.state === 'string'
                ? currentValue.state.trim()
                : currentValue.state || '',
          };
          // Include PrimaryAddressId if it exists
          if (this.userInfo?.PrimaryAddress?.AddressId) {
            changedFields['PrimaryAddressId'] =
              this.userInfo.PrimaryAddress.AddressId;
          }
        }
      } else if (key == 'IsMarketingOptedIn') {
        const optedValue = this.profileForm.get(key)?.getRawValue();

        if (optedValue != this.initialFormValues.IsMarketingOptedIn) {
          if (!changedFields['Declarations']) {
            changedFields['Declarations'] = {};
          }
          changedFields['Declarations'][key] = optedValue;
        }
      } else {
        const currentValue = this.profileForm.get(key)?.getRawValue();
        const trimmedValue =
          typeof currentValue === 'string' ? currentValue.trim() : currentValue;
        let finalValue = trimmedValue;

        if (trimmedValue !== this.initialFormValues[key]) {
          changedFields[key] = finalValue;
        }
      }
    });

    if (this.isFileUploaded && this.selectedFile) {
      // Add the image to changed fields
      changedFields['PictureUrl'] = this.uploadImageUrl;
    }

    // Identify verifiable changes (only email or mobile)
    const verifiableChanges: { field: 'email' | 'mobile'; value: string }[] =
      [];
    if (
      'email' in changedFields &&
      changedFields.email !== this.profileFormDetails['email']
    ) {
      verifiableChanges.push({ field: 'email', value: changedFields.email });
    }
    if (
      'mobile' in changedFields &&
      changedFields.mobile &&
      changedFields.mobile !== this.profileFormDetails['mobile']
    ) {
      verifiableChanges.push({ field: 'mobile', value: changedFields.mobile });
    }

    // Prepare the final update payload excluding unchanged verifiable fields
    const updatePayload: any = { ...changedFields };
    if (verifiableChanges.length > 0) {
      // Only include verified changes in the payload after OTP
      this.verificationItems = verifiableChanges.map((change) => ({
        field: change.field === 'email' ? 'email' : 'mobile number',
        value: change.value,
        deliveryMode: change.field === 'email' ? 'EMAIL' : 'SMS',
        otpStatus: OtpStatus.Initial,
        otpVerificationToken: null,
        otpVerificationSuccessToken: null,
        expiresIn: 0,
      }));
      // Check First verifiable change
      this.checkNextVerifiableField(0);
    } else if (Object.keys(changedFields).length > 0) {
      if (
        'mobile' in changedFields &&
        !changedFields['mobile'] &&
        this.initialFormValues.mobile != changedFields['mobile']
      ) {
        this.deleteUserMobileAndUpdateUser({
          request: updatePayload,
          userId: this.userInfo!.UserId!,
        });
      } else {
        // No verifiable changes, dispatch update with all changed fields
        this.store.dispatch(
          UpdateUserAction({
            request: updatePayload,
            userId: this.userInfo!.UserId!,
          }),
        );
      }
    }
  }
}

@Component({
  selector: 'app-confirm-delete-account-dialog',
  standalone: true,
  template: `
    <h2 mat-dialog-title class="warning-title">
      <mat-icon color="warn" class="warning-icon">warning</mat-icon>
      Delete Account
    </h2>

    <mat-dialog-content class="warning-content">
      Are you sure you want to delete your account? This action cannot be
      undone.
    </mat-dialog-content>

    <mat-dialog-actions align="end">
      <button mat-button mat-dialog-close cdkFocusInitial>No</button>
      <button mat-button color="warn" (click)="onConfirm()">Yes</button>
    </mat-dialog-actions>
  `,
  styles: [
    `
      .warning-title {
        display: flex;
        align-items: center;
        color: #d32f2f;
        font-weight: 500;
        margin: 0;
        margin-top: 10px;
      }

      .warning-icon {
        font-size: 24px;
        line-height: 1;
        vertical-align: middle;
        margin-right: 15px;
      }

      .warning-content {
        margin-top: 8px;
        padding: 5px 20px !important;
      }
    `,
  ],
  imports: [
    MatDialogModule,
    MatDialogActions,
    MatDialogClose,
    MatDialogContent,
    MatDialogTitle,
    MatButtonModule,
    MatIconModule,
  ],
})
export class ConfirmDeleteAccountDialogComponent {
  constructor(
    public dialogRef: MatDialogRef<ConfirmDeleteAccountDialogComponent>,
  ) {}

  onConfirm(): void {
    this.dialogRef.close(true);
  }
}

// Profile Page OTP Dialog
@Component({
  selector: 'profile-otp-dialog',
  template: `
    <div class="otp-dialog-container">
      <div class="header">
        <h4 class="header-title">Verify Your Changes</h4>
        <p class="header-description">
          We need to verify your new email and/or phone for security purposes.
        </p>
      </div>

      <mat-dialog-content>
        <div
          *ngFor="
            let vItem of verificationItems;
            let i = index;
            let last = last
          "
          class="verification-block"
        >
          <div class="section-title">
            <mat-icon>{{
              vItem.fieldName === 'email' ? 'email' : 'phone'
            }}</mat-icon>
            <span>{{
              vItem.fieldName === 'email'
                ? 'Email Verification'
                : 'Phone Verification'
            }}</span>
            <span class="sent-label">{{ localOtpStatuses[i] }}</span>
          </div>
          <div class="description">
            Enter the 6-digit code sent to
            <strong
              >{{ vItem.fieldName === 'email' ? '' : '+91 '
              }}{{ vItem.fieldValue }}</strong
            >
          </div>
          <div class="otp-boxes">
            <ngx-otp-input
              #ngxOtpInput
              [config]="config"
              (onInputChange)="onOtpChange($event, i)"
              [disabled]="localOtpStatuses[i] === OTPStatusEnum.Verified"
              (keydown)="onOtpKeydown($event, i)"
              [ngModel]="localOtpStrings[i] || ''"
            >
            </ngx-otp-input>
          </div>
          <div class="resend-block">
            <span
              *ngIf="localExpiresIn[i] > 0"
              [style.visibility]="
                localOtpStatuses[i] !== OTPStatusEnum.Verified
                  ? 'visible'
                  : 'hidden'
              "
            >
              Resend in {{ localExpiresIn[i] }} seconds
            </span>
            <button
              *ngIf="localExpiresIn[i] === 0"
              mat-raised-button
              [disabled]="
                localOtpStatuses[i] === OTPStatusEnum.Verifying ||
                localOtpStatuses[i] === OTPStatusEnum.Verified
              "
              (click)="resendOTP(i)"
            >
              Resend OTP
            </button>
          </div>
          <button
            mat-raised-button
            color="primary"
            class="verify-btn"
            [ngClass]="{
              'verify-btn-disabled':
                !isOtpFilled(i) ||
                localOtpStatuses[i] === OTPStatusEnum.Verifying ||
                localOtpStatuses[i] === OTPStatusEnum.Verified,
              'verify-btn-margin-btm': !last
            }"
            [disabled]="
              !isOtpFilled(i) ||
              localOtpStatuses[i] === OTPStatusEnum.Verifying ||
              localOtpStatuses[i] === OTPStatusEnum.Verified
            "
            (click)="verifyOtp(i)"
          >
            {{
              localOtpStatuses[i] === OTPStatusEnum.Verifying
                ? 'Verifying...'
                : 'Verify'
            }}
          </button>

          <!-- Add horizontal line if not the last item -->
          <hr *ngIf="!last" class="separator" />
        </div>
      </mat-dialog-content>

      <mat-dialog-actions class="actions">
        <button
          class="cancel-button"
          mat-stroked-button
          mat-dialog-close
          [disabled]="areAllVerified()"
          [ngClass]="{ 'cancel-button-disabled': areAllVerified() }"
          (click)="onCancel()"
        >
          Cancel
        </button>
        <button
          class="complete-button"
          mat-stroked-button
          [ngClass]="{ 'complete-btn-disabled': !areAllVerified() }"
          [disabled]="!areAllVerified()"
          (click)="onCompleteVerification()"
        >
          Complete Verification
        </button>
      </mat-dialog-actions>
    </div>
  `,
  standalone: true,
  imports: [
    MatDialogActions,
    MatDialogContent,
    MatIcon,
    NgOtpInputModule,
    FormsModule,
    CommonModule,
  ],
  styles: [
    `
      .otp-dialog-container {
        padding: 16px 8px;
        border-radius: 16px;
        background-color: rgba(255, 255, 255, 1);
        min-width: 500px;

        hr {
          margin: 0;
        }

        .header {
          padding: 10px 10px 0 10px;

          .header-title {
          }

          .header-description {
            color: #717182;
            font-size: 14px;
            margin: 0px;
          }
        }

        mat-dialog-content {
          padding: 0 10px 0 10px;
        }

        mat-dialog-actions {
          padding: 0 10px;
        }

        .subtitle {
          font-size: 14px;
          margin-top: -10px;
          margin-bottom: 10px;
          color: #666;
        }

        .verification-block {
          margin: 0;
          margin-top: 10px;
          position: relative;

          .section-title {
            display: flex;
            align-items: center;
            font-weight: bold;
            gap: 8px;

            .sent-label {
              background-color: #e6f4ff;
              color: #1a73e8;
              border-radius: 4px;
              padding: 2px 6px;
              font-size: 12px;
              margin-left: auto;
            }
          }

          .description {
            font-size: 13px;
            margin-top: 4px;
            margin-bottom: 10px;
          }

          .otp-boxes {
            display: flex;
            gap: 8px;
            justify-content: center;
            margin-bottom: 20px;

            input {
              width: 40px;
              height: 48px;
              text-align: center;
              font-size: 20px;
              border: 1px solid #ccc;
              border-radius: 6px;
            }
          }

          .resend-block {
            font-size: 12px;
            color: gray;
            margin-top: 10px;
            margin-bottom: 10px;
            width: fit-content;

            button {
              padding: 5px;
              background-color: transparent;
              border: 1px solid rgba(0, 0, 0, 0.1);
              border-radius: 4px;
            }

            button: disabled {
              pointer-events: none;
              opacity: 0.6;
            }
          }

          .verify-btn {
            position: absolute;
            right: 0;
            bottom: 0;
            border-radius: 4px;
            background-color: rgba(249, 175, 99, 1);
            color: white;
            padding: 5px 10px;
            border: none;
          }

          .verify-btn-margin-btm {
            margin-bottom: 10px;
          }

          .verify-btn-disabled {
            opacity: 0.5 !important;
            cursor: not-allowed !important;
          }
        }

        .actions {
          width: 100%;

          button {
            width: calc(50% - 5px);
            margin: 0;
            border-radius: 4px;
            font-size: 12px;
          }

          .cancel-button {
            margin-right: 10px;
            color: black;
            border: 1px solid rgba(0, 0, 0, 0.1);
            background-color: transparent;
            padding: 5px;
          }

          .cancel-button-disabled {
            opacity: 0.5 !important;
            cursor: not-allowed !important;
          }

          .complete-button {
            background-color: #3b3a3a;
            color: white;
            padding: 5px;
            border: 1px solid rgba(0, 0, 0, 0.1);
          }

          .complete-btn-disabled {
            opacity: 0.5 !important;
            cursor: not-allowed !important;
          }
        }

        .separator {
          margin: 15px 0;
          border: 0;
          border-top: 1px solid #ccc;
        }
      }
    `,
  ],
})
export class ProfileOTPDialogComponent implements OnDestroy {
  @ViewChildren('ngxOtpInput') otpInputs!: QueryList<any>;

  config: NgOtpInputConfig = {
    allowNumbersOnly: true,
    length: 6,
    isPasswordInput: false,
    disableAutoFocus: false,
    placeholder: '',
    separator: '',
  };

  verificationItems: any[] = [];
  localOtpStrings: { [key: number]: string | null } = {};
  localOtpStatuses: { [key: number]: OtpStatus } = {};
  localOtpVerificationTokens: { [key: number]: string | null } = {};
  localExpiresIn: { [key: number]: number } = {};
  private timerSubscriptions: Subscription[] = [];
  OTPStatusEnum = OtpStatus;

  constructor(
    public dialogRef: MatDialogRef<ProfileOTPDialogComponent>,
    @Inject(MAT_DIALOG_DATA)
    public data: {
      verificationItems: any[];
      completeSubject: Subject<void>;
    },
  ) {
    this.verificationItems = data.verificationItems;
    this.verificationItems.forEach((item, index) => {
      this.localOtpStrings[index] = null;
      this.localOtpStatuses[index] = item.otpStatusSubject.value;
      this.localOtpVerificationTokens[index] =
        item.otpVerificationTokenSubject.value;
      this.localExpiresIn[index] = item.expiresInSubject.value;

      item.otpStatusSubject.subscribe(
        (status: OtpStatus) => (this.localOtpStatuses[index] = status),
      );
      item.otpVerificationTokenSubject.subscribe(
        (token: string | null) =>
          (this.localOtpVerificationTokens[index] = token),
      );
      item.expiresInSubject.subscribe((expires: number) => {
        this.localExpiresIn[index] = expires;
        this.startTimer(index);
      });
      this.startTimer(index);
    });
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      if (this.otpInputs.length > 0) {
        const first = this.otpInputs.first;
        const eleId = first.getBoxId(0);
        first.focusTo(eleId);
      }
    }, 1000);
  }

  private startTimer(index: number): void {
    const sub = this.timerSubscriptions[index];
    if (sub) sub.unsubscribe();
    this.timerSubscriptions[index] = interval(1000).subscribe(() => {
      if (this.localExpiresIn[index] > 0) {
        this.localExpiresIn[index]--;
      } else {
        this.timerSubscriptions[index].unsubscribe();
      }
    });
  }

  ngOnDestroy(): void {
    this.timerSubscriptions.forEach((sub) => sub?.unsubscribe());
  }

  onOtpChange(otp: string, index: number): void {
    this.localOtpStrings[index] = otp;
  }

  onOtpKeydown(event: KeyboardEvent, index: number): void {
    if (
      event.key === 'Enter' &&
      this.isOtpFilled(index) &&
      this.localOtpStatuses[index] !== OtpStatus.Verifying &&
      this.localOtpStatuses[index] !== OtpStatus.Verified
    ) {
      this.verifyOtp(index);
    }
  }

  verifyOtp(index: number): void {
    const vItem = this.verificationItems[index];
    const token = this.localOtpVerificationTokens[index];
    const otp = this.localOtpStrings[index];
    if (token && otp) {
      vItem.verifyOtpSubject.next({
        verificationToken: token,
        userInput: otp,
        receiver: vItem.fieldValue,
      });
    }
  }

  resendOTP(index: number): void {
    if (this.localExpiresIn[index] === 0) {
      const vItem = this.verificationItems[index];
      vItem.resendOtpSubject.next({
        receiver: vItem.fieldValue,
        deliveryMode: vItem.fieldName === 'email' ? 'EMAIL' : 'SMS',
      });
    }
  }

  isOtpFilled(index: number): boolean {
    return (
      !!this.localOtpStrings[index] && this.localOtpStrings[index]!.length === 6
    );
  }

  areAllVerified(): boolean {
    return this.verificationItems.every(
      (_, i) => this.localOtpStatuses[i] === OtpStatus.Verified,
    );
  }

  onCancel(): void {
    this.dialogRef.close(false);
  }

  onCompleteVerification(): void {
    this.data.completeSubject.next();
  }
}
